import React, { useState, useEffect } from 'react';
import { getUserFriendlyError } from '../utils/errorMessages';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { useTheme } from '../context/ThemeContext';
import { calendarAPI, userAPI, paymentAPI } from '../services/api';
import { BRANDING } from '../config/branding';
import EditProfileModal from '../components/EditProfileModal';
import EmbeddedCheckoutModal from '../components/EmbeddedCheckout';
import MemorySnapshotRedesigned from '../components/MemorySnapshotRedesigned';
import GoogleCalendarIntegration from '../components/GoogleCalendarIntegration';
import { 
  User, Mail, Calendar, CreditCard, HelpCircle, MessageSquare, 
  Sparkles, ArrowLeft, ExternalLink, Send, LifeBuoy, Lightbulb,
  Settings as SettingsIcon, LogOut, Shield, Bell, Loader2, Edit, CheckCircle, AlertCircle, Brain,
  Globe, Languages, Clock, Check
} from 'lucide-react';
import { Textarea } from '../components/ui/textarea';

export default function Settings() {
  const { user, logout, refreshUser } = useAuth();
  const { locale, pricing, setLanguage, setCountry, t } = useLocale();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  
  // Apple Calendar states
  const [appleLoading, setAppleLoading] = useState(false);
  const [appleSyncLoading, setAppleSyncLoading] = useState(false);
  const [showAppleForm, setShowAppleForm] = useState(false);
  const [appleCredentials, setAppleCredentials] = useState({
    apple_id: '',
    app_password: ''
  });
  
  // Support message states
  const [supportMessage, setSupportMessage] = useState('');
  const [featureMessage, setFeatureMessage] = useState('');
  const [sendingSupport, setSendingSupport] = useState(false);
  const [sendingFeature, setSendingFeature] = useState(false);
  
  // Subscription handlers
  const handleUpgradePlan = async (planType) => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });
      
      // Map UI plan names to backend plan IDs
      const planMapping = {
        'Premium Monthly': 'pro_monthly',
        'Premium Yearly': 'pro_annual'
      };
      
      const planId = planMapping[planType];
      if (!planId) {
        throw new Error('Invalid plan type');
      }
      
      // Open embedded checkout modal instead of redirecting
      setSelectedPlan({ id: planId, name: planType });
      setCheckoutOpen(true);
      setLoading(false);
    } catch (error) {
      console.error('Upgrade error:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || error.message || 'Failed to start checkout. Please try again.' 
      });
      setLoading(false);
    }
  };

  const handleCheckoutSuccess = () => {
    setMessage({ type: 'success', text: '🎉 Payment successful! Your subscription is now active.' });
    if (refreshUser) refreshUser();
    loadSubscriptionData();
  };

  const handleCheckoutClose = () => {
    setCheckoutOpen(false);
    setSelectedPlan(null);
  };

  useEffect(() => {
    loadCalendarStatus();
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      const response = await userAPI.getSubscription();
      setSubscriptionData(response.data);
    } catch (error) {
      console.error('Error loading subscription data:', error);
    }
  };

  const loadCalendarStatus = async () => {
    try {
      const response = await calendarAPI.getAllCalendarStatus();
      setCalendarStatus(response.data);
    } catch (error) {
      console.error('Error loading calendar status:', error);
    }
  };

  // Calendar functions (keeping existing logic)
  const handleConnectCalendar = async () => {
    try {
      setLoading(true);
      const response = await calendarAPI.initiateGoogleLogin(user.id);
      const authUrl = response.data.authorization_url;
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error connecting calendar:', error);
      setMessage({ type: 'error', text: 'Failed to connect Google Calendar. Please try again.' });
      setLoading(false);
    }
  };

  const handleSyncCalendar = async () => {
    try {
      setSyncLoading(true);
      await calendarAPI.syncCalendar();
      setMessage({ type: 'success', text: 'Calendar synced successfully!' });
      await loadCalendarStatus();
    } catch (error) {
      console.error('Error syncing calendar:', error);
      setMessage({ type: 'error', text: 'Failed to sync calendar. Please try again.' });
    } finally {
      setSyncLoading(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Google Calendar?')) {
      return;
    }
    try {
      setLoading(true);
      await calendarAPI.disconnectCalendar();
      setCalendarStatus({ connected: false });
      setMessage({ type: 'success', text: 'Google Calendar disconnected successfully.' });
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
      setMessage({ type: 'error', text: 'Failed to disconnect calendar. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleConnectAppleCalendar = async () => {
    if (!appleCredentials.apple_id || !appleCredentials.app_password) {
      setMessage({ type: 'error', text: 'Please enter both Apple ID and App-Specific Password' });
      return;
    }
    try {
      setAppleLoading(true);
      const response = await calendarAPI.connectAppleCalendar(appleCredentials);
      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });
        setShowAppleForm(false);
        setAppleCredentials({ apple_id: '', app_password: '' });
        await loadCalendarStatus();
      }
    } catch (error) {
      console.error('Error connecting Apple Calendar:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to connect Apple Calendar. Please check your credentials.';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setAppleLoading(false);
    }
  };

  const handleSyncAppleCalendar = async () => {
    try {
      setAppleSyncLoading(true);
      const response = await calendarAPI.syncAppleCalendar();
      setMessage({ type: 'success', text: response.data.message });
      await loadCalendarStatus();
    } catch (error) {
      console.error('Error syncing Apple Calendar:', error);
      setMessage({ type: 'error', text: 'Failed to sync Apple Calendar. Please try again.' });
    } finally {
      setAppleSyncLoading(false);
    }
  };

  const handleDisconnectAppleCalendar = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Apple Calendar?')) {
      return;
    }
    try {
      setAppleLoading(true);
      await calendarAPI.disconnectAppleCalendar();
      setMessage({ type: 'success', text: 'Apple Calendar disconnected successfully.' });
      await loadCalendarStatus();
    } catch (error) {
      console.error('Error disconnecting Apple Calendar:', error);
      setMessage({ type: 'error', text: 'Failed to disconnect Apple Calendar. Please try again.' });
    } finally {
      setAppleLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleSendSupport = async () => {
    if (!supportMessage.trim()) {
      setMessage({ type: 'error', text: 'Please enter your message' });
      return;
    }
    
    try {
      setSendingSupport(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          type: 'support',
          message: supportMessage
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setMessage({ type: 'success', text: data.message });
        setSupportMessage('');
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to send message' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to send message. Please try again.' });
    } finally {
      setSendingSupport(false);
    }
  };

  const handleSendFeature = async () => {
    if (!featureMessage.trim()) {
      setMessage({ type: 'error', text: 'Please describe your feature request' });
      return;
    }
    
    try {
      setSendingFeature(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          type: 'feature',
          message: featureMessage
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setMessage({ type: 'success', text: data.message });
        setFeatureMessage('');
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to send request' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to send request. Please try again.' });
    } finally {
      setSendingFeature(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
      navigate('/signin');
    }
  };

  const handleEditProfile = () => {
    setIsEditProfileOpen(true);
  };

  const handleCloseEditProfile = () => {
    setIsEditProfileOpen(false);
  };

  const handleUpdateProfile = async (updatedUser) => {
    // Refresh user data in context
    if (refreshUser) {
      await refreshUser();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50/80 via-cyan-50/60 to-white">
      {/* Clean Header - matches Home theme */}
      <header className="px-4 py-4 flex items-center justify-between glass-strong sticky top-0 z-50 border-b border-white/40">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/home')}
            className="p-2 hover:bg-teal-100/60 rounded-xl transition-all"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex items-center gap-2">
            <SettingsIcon className="w-6 h-6 text-teal-600" />
            <span className="text-lg font-semibold text-gradient-purple">
              {t('nav.settings')}
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="text-right mr-2 hidden sm:block">
            <p className="text-sm font-semibold text-gray-800">{user?.name}</p>
            <p className="text-xs text-gray-500">{user?.email}</p>
          </div>
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-soft">
            <User className="h-4 w-4 text-white" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 pb-8 pt-4 max-w-2xl mx-auto animate-fade-in">
        {/* Message Banner */}
        {message.text && (
          <div className={`mb-4 p-3.5 rounded-xl shadow-soft animate-fade-in ${
            message.type === 'success' 
              ? 'bg-green-50/80 text-green-700 border border-green-200/60' 
              : 'bg-red-50/80 text-red-700 border border-red-200/60'
          }`}>
            <div className="flex items-center justify-between">
              <p className="text-sm">{message.text}</p>
              <button 
                onClick={() => setMessage({ type: '', text: '' })}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Simple Tab Navigation - With localized labels */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => setActiveTab('language')}
            className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === 'language' 
                ? 'bg-teal-100/80 text-teal-700 shadow-soft' 
                : 'bg-white/60 text-gray-600 hover:bg-white/80'
            }`}
          >
            <Globe className="h-4 w-4" />
            🌍
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
              activeTab === 'profile' 
                ? 'bg-teal-100/80 text-teal-700 shadow-soft' 
                : 'bg-white/60 text-gray-600 hover:bg-white/80'
            }`}
          >
            {t('settings.profile')}
          </button>
          <button
            onClick={() => setActiveTab('integrations')}
            className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
              activeTab === 'integrations' 
                ? 'bg-teal-100/80 text-teal-700 shadow-soft' 
                : 'bg-white/60 text-gray-600 hover:bg-white/80'
            }`}
          >
            {t('settings.integrations')}
          </button>
          <button
            onClick={() => setActiveTab('subscription')}
            className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
              activeTab === 'subscription' 
                ? 'bg-teal-100/80 text-teal-700 shadow-soft' 
                : 'bg-white/60 text-gray-600 hover:bg-white/80'
            }`}
          >
            {t('settings.subscription')}
          </button>
          <button
            onClick={() => setActiveTab('support')}
            className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
              activeTab === 'support' 
                ? 'bg-teal-100/80 text-teal-700 shadow-soft' 
                : 'bg-white/60 text-gray-600 hover:bg-white/80'
            }`}
          >
            {t('settings.support')}
          </button>
          <button
            onClick={() => setActiveTab('memory')}
            className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === 'memory' 
                ? 'bg-teal-100/80 text-teal-700 shadow-soft' 
                : 'bg-white/60 text-gray-600 hover:bg-white/80'
            }`}
          >
            <Brain className="h-4 w-4" />
            {t('settings.memory')}
          </button>
          {/* Admin Only - Investor Dashboard */}
          {(user?.is_admin || user?.role === 'admin') && (
            <button
              onClick={() => navigate('/investor-dashboard')}
              className="px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap flex items-center gap-1.5 bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 hover:from-amber-200 hover:to-yellow-200 border border-amber-200"
            >
              📊 Investor Dashboard
            </button>
          )}
        </div>

        {/* Profile Tab - Clean & Calm */}
        {activeTab === 'profile' && (
          <div className="space-y-4 animate-fade-in-up">
            {/* Profile Card */}
            <div className="glass-strong rounded-2xl p-6 border border-white/60 shadow-soft">
              <div className="flex items-center gap-4 mb-6">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-glow-purple">
                  <User className="h-8 w-8 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-800">{user?.name}</h2>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                </div>
                <button
                  onClick={handleEditProfile}
                  className="px-4 py-2.5 bg-teal-100/80 text-teal-700 rounded-xl text-sm font-medium hover:bg-teal-200/80 transition-all hover:-translate-y-0.5"
                >
                  Edit
                </button>
              </div>
              
              {/* Account Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-teal-50/60 p-4 rounded-xl hover:bg-teal-50/80 transition-colors">
                  <p className="text-xs text-gray-500 mb-1">{t('settings.gender')}</p>
                  <p className="font-medium text-gray-800 capitalize">{user?.gender || t('common.none')}</p>
                </div>
                <div className="bg-blue-50/60 p-4 rounded-xl hover:bg-blue-50/80 transition-colors">
                  <p className="text-xs text-gray-500 mb-1">{t('settings.memberSince')}</p>
                  <p className="font-medium text-gray-800">{(user?.createdAt || user?.created_at) ? new Date(user?.createdAt || user?.created_at).toLocaleDateString(locale.locale, { month: 'short', year: 'numeric' }) : '-'}</p>
                </div>
                <div className="bg-green-50/60 p-4 rounded-xl hover:bg-green-50/80 transition-colors">
                  <p className="text-xs text-gray-500 mb-1">{t('settings.status')}</p>
                  <p className="font-medium text-green-600">✓ {t('settings.active')}</p>
                </div>
                <div className="bg-orange-50/60 p-4 rounded-xl hover:bg-orange-50/80 transition-colors">
                  <p className="text-xs text-gray-500 mb-1">{t('settings.plan')}</p>
                  <p className="font-medium text-gray-800">
                    {subscriptionData?.subscription?.plan_id === 'pro_annual' ? `⭐ ${t('subscription.yearly')}` : 
                     subscriptionData?.subscription?.plan_id === 'pro_monthly' ? `⭐ ${t('subscription.monthly')}` : 'Trial'}
                  </p>
                </div>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full p-4 glass-strong rounded-xl text-red-500 font-medium hover:bg-red-50/80 transition-all duration-200 flex items-center justify-center gap-2 border border-white/60 shadow-soft"
            >
              <LogOut className="w-4 h-4" />
              <span>{t('actions.signOut')}</span>
            </button>
          </div>
        )}

            {/* Integrations Tab (keeping existing logic) */}
            {activeTab === 'integrations' && (
              <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-3 bg-gradient-to-br from-teal-500 to-blue-500 rounded-xl">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{t('settings.calendarIntegrations')}</h2>
                    <p className="text-sm text-gray-600">{t('settings.connectCalendars')}</p>
                  </div>
                </div>
                
                {/* Rest of calendar integration logic stays the same */}
                <div className="space-y-6">
                  {/* Google Calendar - Enhanced Component */}
                  <GoogleCalendarIntegration 
                    onStatusChange={(connected) => {
                      setCalendarStatus(prev => ({
                        ...prev,
                        google_calendar: { ...prev?.google_calendar, connected }
                      }));
                    }}
                  />

                  {/* Apple Calendar Card */}
                  <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-teal-300 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-gray-800 rounded-lg">
                          <Calendar className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900">{t('settings.appleCalendar')}</h3>
                          <p className="text-sm text-gray-600">{t('settings.syncEvents')}</p>
                        </div>
                      </div>
                      {calendarStatus?.apple_calendar?.connected && (
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">{t('settings.connected')}</span>
                      )}
                    </div>
                    
                    {calendarStatus?.apple_calendar?.connected ? (
                      <div className="space-y-3">
                        <div className="bg-gray-50 p-4 rounded-lg text-sm">
                          <p className="text-gray-600">Last synced: {formatDate(calendarStatus?.apple_calendar?.last_sync)}</p>
                          <p className="text-gray-600">Events synced: {calendarStatus?.apple_calendar?.event_count || 0}</p>
                        </div>
                        <div className="flex space-x-3">
                          <button
                            onClick={handleSyncAppleCalendar}
                            disabled={appleSyncLoading}
                            className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50 font-medium"
                          >
                            {appleSyncLoading ? `${t('actions.sync')}...` : t('actions.sync')}
                          </button>
                          <button
                            onClick={handleDisconnectAppleCalendar}
                            disabled={appleLoading}
                            className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors font-medium border-2 border-red-200"
                          >
                            {t('actions.disconnect')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {!showAppleForm ? (
                          <button
                            onClick={() => setShowAppleForm(true)}
                            className="w-full px-4 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg hover:from-gray-900 hover:to-black transition-colors font-medium shadow-md"
                          >
                            {t('actions.connect')} {t('settings.appleCalendar')}
                          </button>
                        ) : (
                          <div className="space-y-4">
                            <input
                              type="email"
                              placeholder="Apple ID (email)"
                              value={appleCredentials.apple_id}
                              onChange={(e) => setAppleCredentials({...appleCredentials, apple_id: e.target.value})}
                              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:ring-teal-500"
                            />
                            <input
                              type="password"
                              placeholder="App-Specific Password"
                              value={appleCredentials.app_password}
                              onChange={(e) => setAppleCredentials({...appleCredentials, app_password: e.target.value})}
                              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:ring-teal-500"
                            />
                            <div className="flex space-x-3">
                              <button
                                onClick={handleConnectAppleCalendar}
                                disabled={appleLoading}
                                className="flex-1 px-4 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50 font-medium"
                              >
                                {appleLoading ? `${t('actions.connect')}...` : t('actions.connect')}
                              </button>
                              <button
                                onClick={() => setShowAppleForm(false)}
                                className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                              >
                                {t('actions.cancel')}
                              </button>
                            </div>
                            <p className="text-xs text-gray-600">{t('settings.appleIdHint')}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Subscription Tab */}
            {activeTab === 'subscription' && (
              <div className="space-y-4">
                {/* Trial Status Banner (if on trial) */}
                {subscriptionData?.subscription?.status === 'trial' && subscriptionData?.subscription?.trial_end && (
                  <div className="bg-gradient-to-r from-teal-500/10 to-cyan-500/10 rounded-2xl p-4 border border-teal-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-500/20 rounded-xl flex items-center justify-center">
                          <Clock className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-teal-700">{t('subscription.freeTrialActive')}</p>
                          <p className="text-xs text-teal-600">
                            {Math.max(0, Math.ceil((new Date(subscriptionData.subscription.trial_end) - new Date()) / (1000 * 60 * 60 * 24)))} {t('subscription.trialDaysLeft')}
                          </p>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        Full Pro Access
                      </span>
                    </div>
                  </div>
                )}

                {/* Current Plan - Calm Style */}
                <div className="bg-white/80 backdrop-blur rounded-2xl p-5 border border-teal-100">
                  <p className="text-xs text-gray-500 mb-1">{t('subscription.currentPlan')}</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-800">
                        {subscriptionData?.subscription?.status === 'trial' ? t('subscription.freeTrialActive') :
                         subscriptionData?.subscription?.plan_id === 'pro_annual' ? `Pro ${t('subscription.yearly')}` : 
                         subscriptionData?.subscription?.plan_id === 'pro_monthly' ? `Pro ${t('subscription.monthly')}` : 'No active plan'}
                      </h2>
                      {subscriptionData?.subscription?.current_period_end && subscriptionData?.subscription?.plan_id && subscriptionData?.subscription?.plan_id !== 'free' && subscriptionData?.subscription?.status !== 'trial' && (
                        <p className="text-xs text-gray-500">
                          Renews {new Date(subscriptionData.subscription.current_period_end).toLocaleDateString(locale.locale)}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-teal-600">
                        {subscriptionData?.subscription?.status === 'trial' ? 'Free' :
                         subscriptionData?.subscription?.plan_id === 'pro_annual' 
                          ? `${pricing?.symbol || '$'}${pricing?.yearly || 99}${t('subscription.perYear')}` 
                          : subscriptionData?.subscription?.plan_id === 'pro_monthly' 
                            ? `${pricing?.symbol || '$'}${pricing?.monthly || 9.99}${t('subscription.perMonth')}` 
                            : '—'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Plans - Calm Style */}
                <div className="bg-white/80 backdrop-blur rounded-2xl p-5 border border-teal-100">
                  <h3 className="text-sm font-medium text-gray-600 mb-4">{t('subscription.choosePlan')}</h3>
                  
                  <div className="space-y-3">
                    {/* Monthly Plan */}
                    <div className={`p-4 rounded-xl border-2 transition-all ${
                      subscriptionData?.subscription?.plan_id === 'pro_monthly'
                        ? 'border-teal-500 bg-teal-50' 
                        : 'border-gray-200 hover:border-teal-300'
                    }`}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-800">{t('subscription.monthly')}</h4>
                            {/* Only show "7-day free trial" badge if user hasn't used trial yet */}
                            {!subscriptionData?.trial_used && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                7-day free trial
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{t('subscription.billedMonthly')}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-gray-800">{pricing?.symbol || '$'}{pricing?.monthly || 9.99}</p>
                          <p className="text-xs text-gray-500">{t('subscription.perMonth')}</p>
                        </div>
                      </div>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>Unlimited tasks & projects</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>AI assistant with memory</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>Calendar integrations</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>Priority support</span>
                        </div>
                      </div>
                      
                      {subscriptionData?.subscription?.plan_id === 'pro_monthly' ? (
                        <div className="text-center py-2 text-teal-600 font-medium text-sm">
                          ✓ Current Plan
                        </div>
                      ) : (
                        <button 
                          onClick={() => handleUpgradePlan('Premium Monthly')}
                          className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium transition-all"
                        >
                          {subscriptionData?.trial_used ? 'Subscribe Now' : 'Start Free Trial'}
                        </button>
                      )}
                    </div>

                    {/* Yearly Plan */}
                    <div className={`p-4 rounded-xl border-2 transition-all ${
                      subscriptionData?.subscription?.plan_id === 'pro_annual'
                        ? 'border-teal-500 bg-teal-50' 
                        : 'border-gray-200 hover:border-teal-300'
                    }`}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-800">Yearly</h4>
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                              Save 17%
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Billed annually</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-gray-800">{pricing?.symbol || '$'}{pricing?.yearly || 99}</p>
                          <p className="text-xs text-gray-500">/year</p>
                        </div>
                      </div>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>Everything in Monthly</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>2 months free ($19.98 saved)</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>Early access to new features</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>Priority feature requests</span>
                        </div>
                      </div>
                      
                      {subscriptionData?.subscription?.plan_id === 'pro_annual' ? (
                        <div className="text-center py-2 text-teal-600 font-medium text-sm">
                          ✓ Current Plan
                        </div>
                      ) : (
                        <button 
                          onClick={() => handleUpgradePlan('Premium Yearly')}
                          className="w-full py-3 bg-gray-800 hover:bg-gray-900 text-white rounded-xl font-medium transition-all"
                        >
                          Subscribe Yearly
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Cancel/Manage */}
                {subscriptionData?.subscription?.plan_id && subscriptionData?.subscription?.plan_id !== 'free' && (
                  <p className="text-center text-xs text-gray-400">
                    Need to cancel? Contact support@lifeflo.ai
                  </p>
                )}
              </div>
            )}

            {/* NEW Support Tab */}
            {activeTab === 'support' && (
              <div className="space-y-6">
                {/* Contact Support Card */}
                <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl">
                      <LifeBuoy className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{t('settings.contactSupport')}</h2>
                      <p className="text-sm text-gray-600">{t('settings.sendMessage')}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <Textarea
                      placeholder="Describe your issue or question..."
                      value={supportMessage}
                      onChange={(e) => setSupportMessage(e.target.value)}
                      rows={5}
                      className="w-full border-2 border-gray-200 focus:border-blue-500 rounded-xl p-4 resize-none"
                    />
                    <button
                      onClick={handleSendSupport}
                      disabled={sendingSupport || !supportMessage.trim()}
                      className="w-full h-12 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-xl font-bold shadow-md hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {sendingSupport ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {t('actions.send')}...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          {t('actions.send')}
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Request Feature Card */}
                <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="p-3 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl">
                      <Lightbulb className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{t('settings.requestFeature')}</h2>
                      <p className="text-sm text-gray-600">{t('settings.shareIdeas')}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <Textarea
                      placeholder="Describe the feature you'd like to see..."
                      value={featureMessage}
                      onChange={(e) => setFeatureMessage(e.target.value)}
                      rows={5}
                      className="w-full border-2 border-gray-200 focus:border-teal-500 rounded-xl p-4 resize-none"
                    />
                    <button
                      onClick={handleSendFeature}
                      disabled={sendingFeature || !featureMessage.trim()}
                      className="w-full h-12 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white rounded-xl font-bold shadow-md hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {sendingFeature ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {t('actions.send')}...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5 mr-2" />
                          {t('actions.send')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Memory Tab - What Allora Remembers */}
            {activeTab === 'memory' && (
              <div className="space-y-4 animate-fade-in-up">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('settings.whatAlloraRemembers')}</h2>
                  <p className="text-sm text-gray-500">
                    See and manage the things Allora has learned about you
                  </p>
                </div>
                <MemorySnapshotRedesigned />
                <div className="glass-strong rounded-2xl p-4 border border-white/60">
                  <p className="text-xs text-gray-500 text-center">
                    {theme?.emojis?.heart || '💜'} Your memories help Allora provide personalized assistance. 
                    You can delete any memory at any time.
                  </p>
                </div>
              </div>
            )}

            {/* Language & Region Tab */}
            {activeTab === 'language' && (
              <div className="space-y-4 animate-fade-in-up">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('settings.language')}</h2>
                  <p className="text-sm text-gray-500">
                    {t('settings.chooseLanguageRegion')}
                  </p>
                </div>

                {/* Language Selection */}
                <div className="glass-strong rounded-2xl p-5 border border-white/60 shadow-soft">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl">
                      <Languages className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{t('settings.language')}</h3>
                      <p className="text-xs text-gray-500">{t('settings.chooseLanguage')}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { code: 'en', name: 'English', flag: '🌐' },
                      { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
                      { code: 'fr', name: 'Français', flag: '🇫🇷' },
                      { code: 'ja', name: '日本語', flag: '🇯🇵' },
                      { code: 'ko', name: '한국어', flag: '🇰🇷' },
                      { code: 'pt', name: 'Português', flag: '🇧🇷' },
                      { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩' },
                      { code: 'zh', name: '简体中文', flag: '🇨🇳' },
                      { code: 'es', name: 'Español', flag: '🇪🇸' },
                    ].map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => setLanguage && setLanguage(lang.code)}
                        className={`p-3 rounded-xl border text-left transition-all duration-200 flex items-center gap-2 ${
                          locale?.language === lang.code
                            ? 'bg-teal-50 border-teal-300 shadow-soft'
                            : 'bg-white/60 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-xl">{lang.flag}</span>
                        <span className="font-medium text-gray-800 text-sm">{lang.name}</span>
                        {locale?.language === lang.code && (
                          <Check className="h-4 w-4 text-teal-600 ml-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Auto-Detected Location Info */}
                <div className="glass-strong rounded-2xl p-5 border border-white/60 shadow-soft">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl">
                      <Clock className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{t('settings.detectedLocation') || 'Auto-Detected Location'}</h3>
                      <p className="text-xs text-gray-500">{t('settings.autoDetectedFromDevice') || 'Automatically detected from your device'}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-gray-50/80 rounded-xl">
                      <p className="text-xs text-gray-500 mb-1">{t('settings.country')}</p>
                      <p className="font-medium text-gray-800">{locale?.country || 'US'}</p>
                    </div>
                    <div className="p-3 bg-gray-50/80 rounded-xl">
                      <p className="text-xs text-gray-500 mb-1">{t('settings.currency')}</p>
                      <p className="font-medium text-gray-800">{locale?.symbol}{locale?.currency || 'USD'}</p>
                    </div>
                    <div className="p-3 bg-gray-50/80 rounded-xl col-span-2">
                      <p className="text-xs text-gray-500 mb-1">{t('settings.timezone')}</p>
                      <p className="font-medium text-gray-800">{locale?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}</p>
                    </div>
                  </div>
                </div>

                {/* Pricing Preview */}
                <div className="glass-strong rounded-2xl p-5 border border-white/60 shadow-soft">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl">
                      <CreditCard className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{t('settings.regionalPricing') || 'Your Regional Pricing'}</h3>
                      <p className="text-xs text-gray-500">{t('settings.pricesInLocalCurrency') || 'Prices in your local currency'}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-4 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl border border-teal-100">
                      <p className="text-xs text-teal-600 font-medium mb-1">{t('settings.proMonthly') || 'Pro Monthly'}</p>
                      <p className="text-2xl font-bold text-gray-800">{pricing?.symbol}{pricing?.monthly ? pricing.monthly.toLocaleString() : '0'}</p>
                      <p className="text-xs text-gray-500">{t('settings.perMonth') || 'per month'}</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100">
                      <p className="text-xs text-green-600 font-medium mb-1">{t('settings.proAnnual') || 'Pro Annual'}</p>
                      <p className="text-2xl font-bold text-gray-800">{pricing?.symbol}{pricing?.yearly ? pricing.yearly.toLocaleString() : '0'}</p>
                      <p className="text-xs text-gray-500">{t('settings.perYear') || 'per year'} ({t('common.save') || 'save'} {pricing?.savingsPercent || 0}%)</p>
                    </div>
                  </div>
                  
                  {pricing?.taxInclusive && (
                    <p className="text-xs text-gray-400 text-center mt-3">
                      {t('settings.pricesIncludeTax') || 'Prices include applicable taxes'}
                    </p>
                  )}
                </div>

                <div className="glass-strong rounded-2xl p-4 border border-white/60">
                  <p className="text-xs text-gray-500 text-center">
                    🌍 Your preferences are saved automatically and will be used across the app.
                  </p>
                </div>
              </div>
            )}

            {/* Goals Tab */}
      </main>

      {/* Edit Profile Modal */}
      <EditProfileModal
        isOpen={isEditProfileOpen}
        onClose={handleCloseEditProfile}
        user={user}
        onUpdate={handleUpdateProfile}
      />

      {/* Embedded Checkout Modal */}
      {selectedPlan && (
        <EmbeddedCheckoutModal
          isOpen={checkoutOpen}
          onClose={handleCheckoutClose}
          planId={selectedPlan.id}
          planName={selectedPlan.name}
          onSuccess={handleCheckoutSuccess}
        />
      )}
    </div>
  );
}
